import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Copy, Loader2, Key, Shield, ShieldOff, AlertTriangle, Code, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
};

export default function AdminApiKeys() {
  const navigate = useNavigate();
  const [scopes, setScopes] = useState({});
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newScopes, setNewScopes] = useState([]);
  const [newPseudo, setNewPseudo] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sc, ks] = await Promise.all([
        api.get("/admin/api-scopes"),
        api.get("/admin/api-keys"),
      ]);
      setScopes(sc.data);
      setKeys(ks.data);
    } catch (e) {
      toast.error("Konnte API-Keys nicht laden");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newLabel.trim()) return toast.error("Bezeichnung fehlt");
    if (newScopes.length === 0) return toast.error("Mindestens ein Scope auswählen");
    setCreating(true);
    try {
      const r = await api.post("/admin/api-keys", {
        label: newLabel.trim(),
        scopes: newScopes,
        pseudonymize: newPseudo,
      });
      setRevealedKey({ key: r.data.key, label: r.data.label });
      setNewLabel("");
      setNewScopes([]);
      setNewPseudo(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellung fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (kid, label) => {
    if (!window.confirm(`API-Key "${label}" widerrufen? Externe Programme verlieren sofort den Zugriff.`)) return;
    try {
      await api.delete(`/admin/api-keys/${kid}`);
      toast.success("Widerrufen");
      load();
    } catch {
      toast.error("Widerruf fehlgeschlagen");
    }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(t).then(() => toast.success("In Zwischenablage kopiert"));
  };

  const toggleScope = (s) => {
    setNewScopes(newScopes.includes(s) ? newScopes.filter((x) => x !== s) : [...newScopes, s]);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500">
      <Loader2 className="animate-spin mr-2" /> Lade…
    </div>
  );

  const base = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          data-testid="apikeys-back-btn"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 mb-4"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold">API-Keys & Schnittstellen</h1>
            <p className="text-sm text-zinc-400 mt-1">Datenexport für Buchhaltung, Marketing, Sales & Provisionsabrechnung</p>
          </div>
          <Button
            data-testid="apikeys-docs-toggle"
            onClick={() => setShowDocs(!showDocs)}
            variant="ghost"
            className="text-zinc-300 hover:text-[#00E575]"
          >
            <BookOpen size={16} className="mr-2" />
            {showDocs ? "Doku verbergen" : "Doku anzeigen"}
          </Button>
        </div>

        {showDocs && (
          <div className="mb-6 bg-[#0F1E18] border border-[#00E575]/20 rounded-2xl p-5 text-sm space-y-3" data-testid="apikeys-docs">
            <div className="flex items-center gap-2"><Code size={16} className="text-[#00E575]" /><span className="font-semibold">Externe API – Schnellstart</span></div>
            <div className="text-zinc-400">Alle Endpoints unter <code className="bg-zinc-900 px-1.5 py-0.5 rounded text-zinc-200 text-xs">{base}/api/v1/external/</code> · Header <code className="bg-zinc-900 px-1.5 py-0.5 rounded text-zinc-200 text-xs">X-API-Key</code> erforderlich.</div>
            <div className="space-y-1.5 text-xs font-mono text-zinc-300">
              <div><span className="text-[#00E575]">GET</span> /v1/external/meta — Übersicht</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/customers?format=json|csv&modified_since=ISO</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/projects?status=&limit=500</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/offers?signed_only=true&format=csv</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/orders — nur unterzeichnete Aufträge</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/commissions?from=2026-01&to=2026-03&rep_id=…</div>
              <div><span className="text-[#00E575]">GET</span> /v1/external/products</div>
            </div>
            <div className="text-xs text-zinc-300 pt-2 border-t border-zinc-800">CSV: Semikolon-getrennt (Excel-DE-kompatibel). Pseudonymisierte Keys liefern Kunden-Daten nur als ID + Region.</div>
          </div>
        )}

        {revealedKey && (
          <div className="mb-6 bg-amber-500/10 border border-amber-500/40 rounded-2xl p-5" data-testid="apikeys-revealed">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-semibold text-amber-300">API-Key nur jetzt sichtbar</h3>
                <p className="text-xs text-amber-200/80 mt-1">Notiere oder kopiere den Schlüssel sofort – er kann später nicht mehr abgerufen werden. Bezeichnung: <b>{revealedKey.label}</b></p>
              </div>
            </div>
            <div className="bg-[#09090B] border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-2">
              <code className="text-xs text-zinc-100 font-mono break-all">{revealedKey.key}</code>
              <button
                data-testid="apikeys-copy-revealed"
                onClick={() => copy(revealedKey.key)}
                className="h-9 w-9 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center flex-shrink-0"
              ><Copy size={16} /></button>
            </div>
            <button
              data-testid="apikeys-dismiss-revealed"
              onClick={() => setRevealedKey(null)}
              className="mt-3 text-xs text-amber-300 hover:text-amber-200"
            >Ich habe den Key sicher gespeichert →</button>
          </div>
        )}

        {/* Create form */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-6">
          <h2 className="font-display text-lg font-semibold mb-4">Neuen API-Key anlegen</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1.5 font-bold">Bezeichnung</label>
              <input
                data-testid="apikeys-new-label"
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder='z. B. "DATEV Buchhaltung" oder "HubSpot Marketing"'
                className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
              />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-2 font-bold">Berechtigungen (Scopes)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(scopes).map(([s, label]) => {
                  const active = newScopes.includes(s);
                  return (
                    <button
                      key={s}
                      data-testid={`scope-${s}`}
                      onClick={() => toggleScope(s)}
                      className={`text-left px-3 py-2.5 rounded-lg border transition ${active ? "border-[#00E575] bg-[#00E575]/10" : "border-zinc-700 bg-[#09090B] hover:border-zinc-600"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-zinc-100">{label}</span>
                        <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center ${active ? "bg-[#00E575] border-[#00E575]" : "border-zinc-600"}`}>
                          {active && <span className="text-[#09090B] text-xs font-bold">✓</span>}
                        </div>
                      </div>
                      <div className="text-[12px] text-zinc-300 font-mono mt-0.5">/v1/external/{s}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              data-testid="apikeys-pseudo-toggle"
              onClick={() => setNewPseudo(!newPseudo)}
              className={`w-full text-left rounded-lg border px-3 py-2.5 flex items-start gap-3 transition ${newPseudo ? "border-amber-500/40 bg-amber-500/10" : "border-zinc-700 bg-[#09090B]"}`}
            >
              <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${newPseudo ? "bg-amber-500 border-amber-500" : "border-zinc-600"}`}>
                {newPseudo && <span className="text-[#09090B] text-xs font-bold">✓</span>}
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-100 flex items-center gap-1.5">
                  {newPseudo ? <Shield size={14} className="text-amber-400" /> : <ShieldOff size={14} className="text-zinc-500" />}
                  Kundendaten pseudonymisieren
                </div>
                <div className="text-[12px] text-zinc-300 mt-0.5">Empfohlen für Marketing/Analytics: Name & E-Mail werden zu IDs, nur Region (PLZ-2/Ort-3) wird ausgegeben.</div>
              </div>
            </button>
            <Button
              data-testid="apikeys-create-btn"
              onClick={create}
              disabled={creating}
              className="h-12 w-full bg-[#00E575] text-[#09090B] font-semibold rounded-xl hover:bg-[#00C964] active:scale-[0.97]"
            >
              {creating ? <Loader2 className="animate-spin" /> : <><Plus size={16} className="mr-2" /> API-Key erstellen</>}
            </Button>
          </div>
        </div>

        {/* Existing keys */}
        <h2 className="font-display text-lg font-semibold mb-3">Existierende Keys ({keys.length})</h2>
        {keys.length === 0 ? (
          <div className="bg-[#18181B] border border-dashed border-zinc-800 rounded-2xl p-8 text-center text-zinc-500">
            Noch keine API-Keys vergeben.
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => {
              const active = !k.revoked_at;
              return (
                <div key={k.id} data-testid={`apikey-row-${k.id}`} className={`bg-[#18181B] border rounded-2xl p-4 ${active ? "border-zinc-800" : "border-red-500/20 opacity-60"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Key size={14} className={active ? "text-[#00E575]" : "text-red-400"} />
                        <span className="font-display font-semibold">{k.label}</span>
                        {!active && <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider bg-red-500/20 text-red-300">Widerrufen</span>}
                        {k.pseudonymize && <span className="px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 inline-flex items-center gap-1"><Shield size={10} />Pseudonym</span>}
                      </div>
                      <div className="font-mono text-xs text-zinc-300 mt-1">{k.key_prefix}… · erstellt {fmtDate(k.created_at)}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(k.scopes || []).map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[12px] font-mono">{s}</span>
                        ))}
                      </div>
                      {k.last_used_at && <div className="text-[12px] text-zinc-300 mt-1.5">Zuletzt verwendet: {fmtDate(k.last_used_at)}</div>}
                    </div>
                    {active && (
                      <button
                        data-testid={`apikey-revoke-${k.id}`}
                        onClick={() => revoke(k.id, k.label)}
                        className="h-9 px-3 rounded-lg bg-red-500/15 text-red-300 border border-red-500/40 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-red-500/25"
                      >
                        <Trash2 size={13} /> Widerrufen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
